import { useId } from "react";
import type { MultiLegDraft } from "../../lib/multiLegPosition";
import {
  countPositionExitTriggerSelections,
  describeShortStrikeTrigger,
  getPositionExitTriggerAvailability,
  sharedShortStrikeAnyWarning,
  type CanonicalPositionPayoffAvailability,
  type PositionExitDteOperator,
  type PositionExitShortStrikeSide,
  type WatchPositionExitDraft,
} from "../../lib/watchPositionExitDraft";
import styles from "./WatchPositionTriggerFields.module.css";

export interface WatchPositionTriggerFieldsProps {
  position: MultiLegDraft;
  value: WatchPositionExitDraft;
  payoffAvailability?: CanonicalPositionPayoffAvailability;
  payoffAvailabilityState?: "idle" | "loading" | "ready" | "error";
  onProfitTargetChange: (value: string) => void;
  onLossLimitChange: (value: string) => void;
  onMaxProfitPercentChange: (value: string) => void;
  onMaxLossPercentChange: (value: string) => void;
  onDteThresholdChange: (value: string) => void;
  onDteOperatorChange: (value: PositionExitDteOperator | null) => void;
  onShortStrikeBreachChange: (enabled: boolean) => void;
  onShortStrikeSideChange: (side: PositionExitShortStrikeSide | null) => void;
}

function formatUsd(value: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function payoffHelper(maximumUsd: string | null | undefined, percent: string, outcome: "profit" | "loss") {
  if (!maximumUsd) return `For example, ${outcome === "profit" ? "50" : "35"} means ${outcome === "profit" ? "half of maximum profit" : "35% of maximum loss"}.`;
  const maximum = Number(maximumUsd);
  const parsedPercent = Number(percent);
  const denominator = `Maximum ${outcome}: ${formatUsd(maximumUsd)} for the whole position, fees excluded.`;
  if (!/^\d+(?:\.\d{1,4})?$/.test(percent.trim()) || parsedPercent < 0 || parsedPercent > 100) {
    return denominator;
  }
  return `${denominator} ${percent.trim()}% equals ${formatUsd(String(maximum * parsedPercent / 100))}.`;
}

export default function WatchPositionTriggerFields({
  position,
  value,
  payoffAvailability,
  payoffAvailabilityState = "idle",
  onProfitTargetChange,
  onLossLimitChange,
  onMaxProfitPercentChange,
  onMaxLossPercentChange,
  onDteThresholdChange,
  onDteOperatorChange,
  onShortStrikeBreachChange,
  onShortStrikeSideChange,
}: WatchPositionTriggerFieldsProps) {
  const instanceId = useId();
  const priceHeadingId = `${instanceId}-position-price-alerts`;
  const riskHeadingId = `${instanceId}-position-risk-alerts`;
  const timeHeadingId = `${instanceId}-position-time-alerts`;
  const strikeHeadingId = `${instanceId}-position-strike-alerts`;
  const countId = `${instanceId}-position-trigger-count`;
  const availability = getPositionExitTriggerAvailability(position, payoffAvailability);
  const hasBothShortSides = availability.shortStrikeSides.length > 1;
  const sharedStrikeWarning = sharedShortStrikeAnyWarning(position, value);
  const shortStrikeToggleCopy = availability.shortStrikeSides.length === 1
    ? `Alert when the ${describeShortStrikeTrigger(position, availability.shortStrikeSides[0]).replace("Underlying is ", "underlying is ")}`
    : "Alert when the underlying reaches a selected short-strike boundary";
  const selectedCount = countPositionExitTriggerSelections(value);
  const atLimit = selectedCount >= 5;
  const disabledWhenEmpty = (selected: boolean) => atLimit && !selected;
  const dteSelected = Boolean(value.dteThreshold.trim() || value.dteOperator);
  const bothPercentUnavailable = !availability.maxProfitPercent && !availability.maxLossPercent;
  const unavailableMessage = (outcome: "profit" | "loss" | "either") => {
    if (position.structure === "single") return "Percent-of-max alerts aren’t available for single options. Dollar alerts above cover this position.";
    if (payoffAvailabilityState === "loading") return "Checking whether this position has a known maximum profit and loss…";
    if (payoffAvailabilityState === "error") return "We couldn’t check this position’s maximums right now. Check the position details or try again.";
    const reason = outcome === "profit"
      ? payoffAvailability?.maxProfitUnavailableReason
      : payoffAvailability?.maxLossUnavailableReason;
    if (reason === "ZERO_MAX_PROFIT") return "This entry basis has no profitable outcome at expiration, so there is no maximum-profit percentage to monitor. This describes expiration payoff, not interim prices.";
    if (reason === "ZERO_MAX_LOSS") return "This entry basis leaves no maximum loss to measure against.";
    return outcome === "either"
      ? "Percent-of-max alerts aren’t available for this position."
      : `Percent-of-max ${outcome} alerts aren’t available for this position.`;
  };
  const stalePercentRemovals = (
    <>
      {!availability.maxProfitPercent && value.maxProfitPercent.trim()
        ? <button type="button" onClick={() => onMaxProfitPercentChange("")}>Remove maximum-profit alert ({value.maxProfitPercent}%)</button>
        : null}
      {!availability.maxLossPercent && value.maxLossPercent.trim()
        ? <button type="button" onClick={() => onMaxLossPercentChange("")}>Remove maximum-loss alert ({value.maxLossPercent}%)</button>
        : null}
    </>
  );

  return (
    <div className={styles.root}>
      <p id={countId} className={styles.triggerCount} role="status" aria-live="polite">
        {selectedCount} of 5 alerts selected{atLimit ? ". Remove one before choosing another." : "."}
      </p>
      <section className={styles.group} aria-labelledby={priceHeadingId}>
        <div className={styles.heading}>
          <strong id={priceHeadingId}>Profit or loss</strong>
          <span>Whole-position estimates in dollars.</span>
        </div>
        <div className={styles.twoColumns}>
          <label>
            <span>Profit target <small>Optional</small></span>
            <input value={value.profitTarget} onChange={(event) => onProfitTargetChange(event.target.value)} inputMode="decimal" placeholder="250" aria-label="Profit target in dollars" aria-describedby={countId} disabled={disabledWhenEmpty(Boolean(value.profitTarget.trim()))} />
            <small>Alert when estimated profit reaches this amount.</small>
          </label>
          <label>
            <span>Loss limit <small>Optional</small></span>
            <input value={value.lossLimit} onChange={(event) => onLossLimitChange(event.target.value)} inputMode="decimal" placeholder="150" aria-label="Loss limit in dollars" aria-describedby={countId} disabled={disabledWhenEmpty(Boolean(value.lossLimit.trim()))} />
            <small>Alert when estimated loss reaches this amount.</small>
          </label>
        </div>
      </section>

      {bothPercentUnavailable ? (
        <p className={styles.unavailableNote} role="status">
          <span>{unavailableMessage("either")}</span>
          {stalePercentRemovals}
        </p>
      ) : (
        <section className={styles.group} aria-labelledby={riskHeadingId}>
          <div className={styles.heading}>
            <strong id={riskHeadingId}>Share of maximum outcome</strong>
            <span>Available when this position has a known maximum profit or loss.</span>
          </div>
          <div className={styles.twoColumns}>
            {availability.maxProfitPercent ? <label>
              <span>Maximum profit reached <small>Optional</small></span>
              <span className={styles.suffixInput}><input value={value.maxProfitPercent} onChange={(event) => onMaxProfitPercentChange(event.target.value)} inputMode="decimal" placeholder="50" aria-label="Maximum profit percentage" aria-describedby={countId} disabled={disabledWhenEmpty(Boolean(value.maxProfitPercent.trim()))} /><span aria-hidden="true">%</span></span>
              <small aria-live="polite">{payoffHelper(payoffAvailability?.maxProfitUsd, value.maxProfitPercent, "profit")}</small>
            </label> : <div className={styles.unavailable} role="status"><span>{unavailableMessage("profit")}</span>{value.maxProfitPercent.trim() && <button type="button" onClick={() => onMaxProfitPercentChange("")}>Remove maximum-profit alert ({value.maxProfitPercent}%)</button>}</div>}
            {availability.maxLossPercent ? <label>
              <span>Maximum loss reached <small>Optional</small></span>
              <span className={styles.suffixInput}><input value={value.maxLossPercent} onChange={(event) => onMaxLossPercentChange(event.target.value)} inputMode="decimal" placeholder="35" aria-label="Maximum loss percentage" aria-describedby={countId} disabled={disabledWhenEmpty(Boolean(value.maxLossPercent.trim()))} /><span aria-hidden="true">%</span></span>
              <small aria-live="polite">{payoffHelper(payoffAvailability?.maxLossUsd, value.maxLossPercent, "loss")}</small>
            </label> : <div className={styles.unavailable} role="status"><span>{unavailableMessage("loss")}</span>{value.maxLossPercent.trim() && <button type="button" onClick={() => onMaxLossPercentChange("")}>Remove maximum-loss alert ({value.maxLossPercent}%)</button>}</div>}
          </div>
        </section>
      )}

      <section className={styles.group} aria-labelledby={timeHeadingId}>
        <div className={styles.heading}>
          <strong id={timeHeadingId}>Time to expiration</strong>
          <span>Use a whole number of calendar days.</span>
        </div>
        <div className={styles.dteRow}>
          <label>
            <span>Days remaining <small>Optional</small></span>
            <input value={value.dteThreshold} onChange={(event) => onDteThresholdChange(event.target.value)} inputMode="numeric" placeholder="7" aria-label="Days to expiration" aria-describedby={countId} disabled={disabledWhenEmpty(dteSelected)} />
          </label>
          <fieldset>
            <legend>Match</legend>
            <label><input type="radio" name={`${instanceId}-position-dte-operator`} checked={value.dteOperator === "lte"} onChange={() => onDteOperatorChange("lte")} disabled={disabledWhenEmpty(dteSelected)} />That many or fewer</label>
            <label><input type="radio" name={`${instanceId}-position-dte-operator`} checked={value.dteOperator === "eq"} onChange={() => onDteOperatorChange("eq")} disabled={disabledWhenEmpty(dteSelected)} />Exactly that many</label>
          </fieldset>
        </div>
      </section>

      {availability.shortStrikeSides.length > 0 && <section className={styles.group} aria-labelledby={strikeHeadingId}>
        <div className={styles.heading}>
          <strong id={strikeHeadingId}>Short-strike level</strong>
          <span>Checks the current sampled underlying price; it does not predict assignment or require a new crossing.</span>
        </div>
        <label className={styles.toggle}>
          <input type="checkbox" checked={value.shortStrikeBreach} onChange={(event) => onShortStrikeBreachChange(event.target.checked)} aria-describedby={countId} disabled={disabledWhenEmpty(value.shortStrikeBreach)} />
          <span>{shortStrikeToggleCopy}</span>
        </label>
        {value.shortStrikeBreach && hasBothShortSides && <fieldset className={styles.sideChoices}>
          <legend>Which short strike?</legend>
          <label><input type="radio" name={`${instanceId}-position-short-strike-side`} checked={value.shortStrikeSide === "PUT"} onChange={() => onShortStrikeSideChange("PUT")} />Put — {describeShortStrikeTrigger(position, "PUT").replace("Underlying is ", "")}</label>
          <label><input type="radio" name={`${instanceId}-position-short-strike-side`} checked={value.shortStrikeSide === "CALL"} onChange={() => onShortStrikeSideChange("CALL")} />Call — {describeShortStrikeTrigger(position, "CALL").replace("Underlying is ", "")}</label>
          <label><input type="radio" name={`${instanceId}-position-short-strike-side`} checked={value.shortStrikeSide === "ANY"} onChange={() => onShortStrikeSideChange("ANY")} />Either boundary</label>
        </fieldset>}
        {sharedStrikeWarning && <p className={styles.warning} role="status">{sharedStrikeWarning}</p>}
      </section>}
    </div>
  );
}
